import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Download, ArrowDownLeft, ArrowUpRight, StickyNote } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import api, { fmtNum, fmtDate } from "../lib/api";

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthOptions = () => {
  const opts = [];
  const d = new Date();
  for (let i = 12; i >= -18; i--) {
    const m = new Date(d.getFullYear(), d.getMonth() + i, 1);
    opts.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return opts;
};

const MONTHS_HR = ["siječanj", "veljača", "ožujak", "travanj", "svibanj", "lipanj", "srpanj", "kolovoz", "rujan", "listopad", "studeni", "prosinac"];
const label = (m) => `${MONTHS_HR[parseInt(m.slice(5)) - 1]} ${m.slice(0, 4)}.`;
const DAYS_HR = ["ned", "pon", "uto", "sri", "čet", "pet", "sub"];
const dayName = (iso) => DAYS_HR[new Date(iso + "T12:00:00Z").getUTCDay()];
const isWeekend = (iso) => [0, 6].includes(new Date(iso + "T12:00:00Z").getUTCDay());
const TODAY = new Date().toISOString().slice(0, 10);

export default function DailyBalancePage() {
  const [month, setMonth] = useState(thisMonth());
  const [horizon, setHorizon] = useState("1");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);
  const [dayDate, setDayDate] = useState("");
  const [dayTxs, setDayTxs] = useState([]);
  const [monthNote, setMonthNote] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [showAllBanks, setShowAllBanks] = useState(false);

  useEffect(() => {
    api.get("/daily-balances", { params: { month, months: horizon } }).then((r) => setData(r.data));
    api.get("/month-notes", { params: { month } }).then((r) => setMonthNote(r.data.text || ""));
  }, [month, horizon]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await api.get("/export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vtz-cashflow-${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel datoteka preuzeta");
    } catch {
      toast.error("Greška pri izvozu");
    } finally {
      setExporting(false);
    }
  };

  const openDay = async (d) => {
    if (!d.tx_count) return;
    setDayDate(d.date);
    const r = await api.get("/transactions", { params: { month: d.date.slice(0, 7), limit: 1000 } });
    setDayTxs(r.data.filter((t) => t.date === d.date));
    setDayOpen(true);
  };

  const bankShort = (id) => data?.banks.find((b) => b.id === id)?.short || "?";

  const hasBankData = (b) => {
    const o = data.opening.banks[b.id];
    if (o && (Math.abs(o.eur) > 0.004 || Math.abs(o.bam) > 0.004)) return true;
    return data.days.some((d) => { const v = d.banks[b.id]; return v && (Math.abs(v.eur) > 0.004 || Math.abs(v.bam) > 0.004); });
  };
  const inactiveBanks = data ? data.banks.filter((b) => !hasBankData(b)) : [];
  const visibleBanks = data ? (showAllBanks ? data.banks : data.banks.filter(hasBankData)) : [];
  const rows = data ? (onlyActive ? data.days.filter((d) => d.tx_count > 0 || d.planned_count > 0) : data.days) : [];

  return (
    <div className="space-y-6 fade-up" data-testid="daily-balance-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Dan po dan</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Dnevni saldo</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="daily-month-select" className="w-48 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions().map((m) => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={horizon} onValueChange={setHorizon}>
            <SelectTrigger data-testid="daily-horizon-select" className="w-36 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 mjesec</SelectItem>
              <SelectItem value="3">3 mjeseca</SelectItem>
              <SelectItem value="6">6 mjeseci</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="export-excel-btn" onClick={exportExcel} disabled={exporting} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
            <Download className="w-4 h-4 mr-2" /> {exporting ? "Izvoz..." : "Izvoz u Excel"}
          </Button>
        </div>
      </div>

      {monthNote && (
        <div className="flex items-start gap-2 bg-[#F8FAFC] border border-[#D97706]/30 rounded-sm px-4 py-3" data-testid="daily-month-note">
          <StickyNote className="w-4 h-4 text-[#D97706] shrink-0 mt-0.5" />
          <p className="text-sm text-[#0F172A] italic">{monthNote}</p>
        </div>
      )}

      {data && (
        <>
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
            <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-4">Kretanje ukupnog salda (EUR)</h2>
            {data.days.length === 0 ? (
              <p className="text-sm text-[#64748B]">Nema podataka za ovaj mjesec.</p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.days}>
                    <defs>
                      <linearGradient id="saldoFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0D9488" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#0D9488" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => (horizon !== "1" ? d.slice(5) : d.slice(8))} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} domain={["auto", "auto"]} width={80} />
                    <Tooltip formatter={(v, n) => [fmtNum(v, "€"), n === "projected_eur" ? "Projekcija" : "Ukupno"]} labelFormatter={(d) => `${fmtDate(d)} (${dayName(d)})`} />
                    <Area type="monotone" dataKey="total_eur" stroke="#0D9488" strokeWidth={2} fill="url(#saldoFill)" />
                    {data.has_planned && (
                      <Area type="monotone" dataKey="projected_eur" stroke="#D97706" strokeWidth={2} strokeDasharray="6 3" fill="none" />
                    )}
                    {data.threshold != null && (
                      <ReferenceLine y={data.threshold} stroke="#DC2626" strokeDasharray="4 4" label={{ value: `prag ${fmtNum(data.threshold)} €`, position: "insideTopRight", fill: "#DC2626", fontSize: 11 }} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-[#475569] cursor-pointer select-none">
              <Switch data-testid="daily-only-active-toggle" checked={onlyActive} onCheckedChange={setOnlyActive} />
              Samo dani s prometom
            </label>
            {inactiveBanks.length > 0 && (
              <p className="text-xs text-[#64748B]" data-testid="daily-hidden-banks-note">
                {showAllBanks ? "Prikazane su i banke bez prometa" : `Skriveno (bez prometa): ${inactiveBanks.map((b) => b.short).join(", ")}`}
                {" — "}
                <button data-testid="daily-toggle-banks-btn" onClick={() => setShowAllBanks(!showAllBanks)} className="underline underline-offset-2 hover:text-[#0D9488]">
                  {showAllBanks ? "sakrij" : "prikaži"}
                </button>
              </p>
            )}
          </div>

          <div className="md:hidden space-y-1.5" data-testid="daily-cards">
            <div className="bg-[#F1F5F9] rounded-sm px-3 py-2 flex justify-between text-sm font-medium">
              <span>Početno stanje</span>
              <span className="tabular-nums font-semibold">{fmtNum(data.opening.total_eur)} €</span>
            </div>
            {rows.map((d) => (
              <button
                key={d.date}
                data-testid={`daily-card-${d.date}`}
                onClick={() => openDay(d)}
                className={`w-full text-left bg-white border rounded-sm px-3 py-2 transition-colors duration-200 ${d.date === TODAY ? "border-[#0D9488] ring-1 ring-[#0D9488]" : "border-[#E2E8F0]"} ${d.tx_count === 0 && d.date !== TODAY ? "opacity-60" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium tabular-nums">
                    {fmtDate(d.date)} <span className={`text-xs ${isWeekend(d.date) ? "text-[#D97706]" : "text-[#64748B]"}`}>{dayName(d.date)}</span>
                    {d.date === TODAY && <span className="ml-1.5 text-[10px] font-bold text-[#0D9488] uppercase tracking-wider">danas</span>}
                  </span>
                  <span className="text-sm font-semibold tabular-nums">{fmtNum(d.total_eur)} €</span>
                </div>
                {(d.inflow > 0 || d.outflow > 0 || (data.has_planned && d.planned_count > 0)) && (
                  <div className="flex items-center gap-3 mt-1 text-xs tabular-nums">
                    {d.inflow > 0 && <span className="text-[#0D9488] font-medium">+{fmtNum(d.inflow)}</span>}
                    {d.outflow > 0 && <span className="text-[#DC2626] font-medium">−{fmtNum(d.outflow)}</span>}
                    {data.has_planned && <span className="text-[#D97706] ml-auto">proj. {fmtNum(d.projected_eur)}</span>}
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="hidden md:block bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
                  <TableHead rowSpan={2} className="border-r border-[#E2E8F0]">Datum</TableHead>
                  <TableHead rowSpan={2} className="border-r border-[#E2E8F0]">Dan</TableHead>
                  {visibleBanks.map((b) => (
                    <TableHead key={b.id} colSpan={2} className="text-center border-r border-[#E2E8F0]">{b.short}</TableHead>
                  ))}
                  <TableHead rowSpan={2} className="text-right border-r border-[#E2E8F0]">Priljev €</TableHead>
                  <TableHead rowSpan={2} className="text-right border-r border-[#E2E8F0]">Odljev €</TableHead>
                  <TableHead rowSpan={2} className={`text-right ${data.has_planned ? "border-r border-[#E2E8F0]" : ""}`}>Ukupno €</TableHead>
                  {data.has_planned && <TableHead rowSpan={2} className="text-right text-[#D97706]">Projekcija €</TableHead>}
                </TableRow>
                <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
                  {visibleBanks.map((b) => (
                    <Fragment key={b.id}>
                      <TableHead className="text-right text-xs">EUR</TableHead>
                      <TableHead className="text-right text-xs border-r border-[#E2E8F0]">BAM</TableHead>
                    </Fragment>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody data-testid="daily-balance-table-body">
                <TableRow className="bg-[#F8FAFC] font-medium">
                  <TableCell colSpan={2} className="text-sm border-r border-[#E2E8F0]">Početno stanje</TableCell>
                  {visibleBanks.map((b) => (
                    <Fragment key={b.id}>
                      <TableCell className="text-right tabular-nums text-sm">{fmtNum(data.opening.banks[b.id]?.eur)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm border-r border-[#E2E8F0]">{fmtNum(data.opening.banks[b.id]?.bam)}</TableCell>
                    </Fragment>
                  ))}
                  <TableCell className="border-r border-[#E2E8F0]"></TableCell>
                  <TableCell className="border-r border-[#E2E8F0]"></TableCell>
                  <TableCell className="text-right tabular-nums text-sm font-semibold" data-testid="opening-total">{fmtNum(data.opening.total_eur)}</TableCell>
                  {data.has_planned && <TableCell className="text-right tabular-nums text-sm text-[#D97706]">{fmtNum(data.opening.projected_eur)}</TableCell>}
                </TableRow>
                {rows.map((d) => (
                  <TableRow
                    key={d.date}
                    data-testid={`daily-row-${d.date}`}
                    onClick={() => openDay(d)}
                    className={`transition-colors duration-200 hover:bg-[#F1F5F9] ${d.tx_count > 0 ? "cursor-pointer" : ""} ${d.date === TODAY ? "bg-[#0D9488]/10" : isWeekend(d.date) ? "bg-[#F8FAFC]" : ""} ${d.tx_count === 0 && d.date !== TODAY ? "text-[#64748B] opacity-70" : ""}`}
                  >
                    <TableCell className={`tabular-nums text-sm border-r border-[#E2E8F0] ${d.date === TODAY ? "font-bold text-[#0D9488]" : ""}`}>{fmtDate(d.date)}</TableCell>
                    <TableCell className={`text-sm border-r border-[#E2E8F0] ${isWeekend(d.date) ? "text-[#D97706]" : ""}`}>
                      {dayName(d.date)}
                      {d.date === TODAY && <span data-testid="daily-today-badge" className="ml-1.5 text-[10px] font-bold text-[#0D9488] uppercase tracking-wider">danas</span>}
                    </TableCell>
                    {visibleBanks.map((b) => (
                      <Fragment key={b.id}>
                        <TableCell className={`text-right tabular-nums text-sm ${d.tx_count ? "" : "text-[#64748B]"}`}>{fmtNum(d.banks[b.id]?.eur)}</TableCell>
                        <TableCell className={`text-right tabular-nums text-sm border-r border-[#E2E8F0] ${d.tx_count ? "" : "text-[#64748B]"}`}>{fmtNum(d.banks[b.id]?.bam)}</TableCell>
                      </Fragment>
                    ))}
                    <TableCell className="text-right tabular-nums text-sm text-[#0D9488] border-r border-[#E2E8F0]">{d.inflow ? `+${fmtNum(d.inflow)}` : ""}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm text-[#DC2626] border-r border-[#E2E8F0]">{d.outflow ? `−${fmtNum(d.outflow)}` : ""}</TableCell>
                    <TableCell className={`text-right tabular-nums text-sm font-semibold ${d.tx_count ? "text-[#0F172A]" : "text-[#64748B]"}`}>{fmtNum(d.total_eur)}</TableCell>
                    {data.has_planned && (
                      <TableCell className={`text-right tabular-nums text-sm ${data.threshold != null && d.projected_eur < data.threshold ? "font-semibold text-[#DC2626] bg-[#DC2626]/10" : d.planned_count ? "font-semibold text-[#D97706]" : "text-[#B08640]"}`} data-testid={`projected-${d.date}`}>
                        {fmtNum(d.projected_eur)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={dayOpen} onOpenChange={setDayOpen}>
            <DialogContent className="rounded-sm max-w-lg" data-testid="day-detail-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Transakcije — {fmtDate(dayDate)} ({dayName(dayDate || "2026-01-01")})</DialogTitle>
                <DialogDescription className="sr-only">Popis transakcija za odabrani dan</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="day-transactions-list">
                {dayTxs.length === 0 && <p className="text-sm text-[#64748B]">Nema transakcija za ovaj dan.</p>}
                {dayTxs.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 border border-[#E2E8F0] rounded-sm p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {t.type === "naplata" ? (
                        <ArrowDownLeft className="w-4 h-4 text-[#0D9488] shrink-0" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-[#DC2626] shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#0F172A] truncate">{t.counterparty || t.category}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className="bg-[#F1F5F9] text-[#475569] hover:bg-[#F1F5F9] rounded-sm text-[10px] px-1.5 py-0">{bankShort(t.bank_id)}</Badge>
                          <span className="text-xs text-[#64748B] truncate" title={t.note || ""}>{t.category}{t.note ? ` · ${t.note}` : ""}</span>
                        </div>
                      </div>
                    </div>
                    <p className={`text-sm font-semibold tabular-nums shrink-0 ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                      {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}
                    </p>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
